'use strict';

/*
  ContentCenterBanner

  An object that represents an individual ContentCenterBanner. Data for this resource is stored through the
  Presenter/Model. Provides convenience methods for working with the resource.

  Parameters:
    presenter [APIPresenter] (required):
      Reference to the APIPresenter where the data for this content center banner is stored.

    contentBannerModel (required):
      Reference to model of the content center banner
*/
function ContentCenterBanner(presenter, bannerModel) {
  this._BANNERS_MODEL = 'content_center.banners.content_slides';
  this._BANNER_CONTENT_MODEL = 'content_center.banner_content.items';
  this._bannerIndex = null;
  this._bannerContentIndex = null;

  /*
    Constructor
  */
  this.constructor = function (presenter, bannerModel) {
    this.presenter = presenter;
    this.bannerModel = bannerModel;
    this._bannerIndex = this._getBannerArrayIndex();

    // Bind banner to content index
    var bannerSlideId = this._getContentfulKey();
    var bannerContentModel = this.presenter.getModelProperty(this._BANNER_CONTENT_MODEL);
    for (var i = 0; i < bannerContentModel.length; i++) {
      if (bannerContentModel[i].id === bannerSlideId) {
        this._bannerContentIndex = i;
        break;
      }
    }
  };

  /*
    Returns the ID for the banner.
  */
  this.getId = function () {
    var index = this._getBannerArrayIndex();
    return this.presenter.getModelProperty(this._BANNERS_MODEL + '[' + index + '].id');
  };

  /*
    Returns a object with key/value pairs of content. Using an object instead of getters for individual
    keys because in the future, different slides may contain different keys for different content.
     Eg.
    {
      title: 'Title of slide',
      content: 'You are pre-approved for $3000.',
      imageUrl: 'https://url.to/image.png'
    }
  */
  this.getContent = function () {
    return {
      title: this.presenter.getModelProperty(this._BANNER_CONTENT_MODEL + '[' + this._bannerContentIndex + '].title'),
      content: this._getDescription(),
      useStaticMediaOnWeb: this.presenter.getModelProperty(this._BANNER_CONTENT_MODEL + '[' + this._bannerContentIndex + '].useStaticMediaOnWeb'),
      mediaType: this.presenter.getModelProperty(this._BANNER_CONTENT_MODEL + '[' + this._bannerContentIndex + '].media.file.contentType'),
      mediaUrl: this.presenter.getModelProperty(this._BANNER_CONTENT_MODEL + '[' + this._bannerContentIndex + '].media.file.url'),
      alternativeMediaUrl: this.presenter.getModelProperty(this._BANNER_CONTENT_MODEL + '[' + this._bannerContentIndex + '].alternativeMedia.file.url'),
      imageTitle: this.presenter.getModelProperty(this._BANNER_CONTENT_MODEL + '[' + this._bannerContentIndex + '].media.title')
    };
  };

  /*
    Returns a string denoting the category.
  */
  this.getCategory = function () {
    return this.presenter.getModelProperty(this._BANNER_CONTENT_MODEL + '[' + this._bannerContentIndex + '].category');
  };

  /*
    Returns an array of navigation actions for this content center slide
     Eg.
    [
      {
          "id": "nav-action-identifier",
          "url": "/member/dashboard/loans"
      }
      ...
    ]
  */
  this.getNavigationActions = function () {
    return this.presenter.getModelProperty(this._BANNER_CONTENT_MODEL + '[' + this._bannerContentIndex + '].configuration.navigationActions');
  };

  /*
    Returns an referral content for this content center slide
     Eg.
    [
      {
          "emailSubject": "Email subject",
          "emailDescription": "Email body",
          "twitterSubject": "Message for twitter"
      }
      ...
    ]
  */
  this.getReferralContent = function () {
    return this.presenter.getModelProperty(this._BANNER_CONTENT_MODEL + '[' + this._bannerContentIndex + '].configuration.navigationActions[0].content');
  };

  /*
    Returns main navigation link for banner.
  */
  this.getBannerLink = function () {
    var linkUrl = '';
    var navActions = this.getNavigationActions();

    if (!navActions) {
      console.warn('ContentCenterBanner.Js: unable to retrieve nav action for banner index ' + this._bannerIndex);
    } else {
      var linkIndex = navActions.findIndex(function (action) {
        return action.id === 'nav-action-main';
      });
      if (linkIndex > -1) {
        linkUrl = navActions[linkIndex].targetId;
      }
      if (linkUrl && linkUrl.indexOf('http') !== 0) {
        linkUrl = '/' + linkUrl;
      }
    }
    return linkUrl;
  };

  /*
    Returns whether or not this slide can be dismissed.
  */
  this.isDismissible = function () {
    var index = this._getBannerArrayIndex();
    return this.presenter.getModelProperty(this._BANNERS_MODEL + '[' + index + '].dismissible');
  };

  /*
    Returns whether or not this slide has already been dismissed.
  */
  this.isDismissed = function () {
    var index = this._getBannerArrayIndex();
    return this.presenter.getModelProperty(this._BANNERS_MODEL + '[' + index + '].dismissed') || false;
  };

  this._getContentfulKey = function () {
    var index = this._getBannerArrayIndex();
    return this.presenter.getModelProperty(this._BANNERS_MODEL + '[' + index + '].contentful_key');
  };

  this._getContentValues = function () {
    var index = this._getBannerArrayIndex();
    return this.presenter.getModelProperty(this._BANNERS_MODEL + '[' + index + '].content_values');
  };

  this._getContentfulUpdatedAt = function () {
    var index = this._getBannerArrayIndex();
    return this.presenter.getModelProperty('content_center.banner_content._memory.items[' + index + ']._updatedAt');
  };

  this._getDescription = function () {
    var description = this.presenter.getModelProperty(this._BANNER_CONTENT_MODEL + '[' + this._bannerContentIndex + '].description');

    if (description) {
      var contentValues = this._getContentValues();
      description = description.replace(/\[(.+?)\]/g, function (match, value) {
        return contentValues[value.toLowerCase()] ? contentValues[value.toLowerCase()] : '';
      });
    } else {
      console.warn('ContentCenterBanner.Js: unable to retrieve description for banner index ' + this._bannerIndex);
    }

    return description;
  };

  this._getBannerArrayIndex = function () {
    if (this._bannerIndex === null || this.presenter.getModelProperty('content_center.banners.content_slides[' + this._bannerIndex + '].id') !== this.id) {
      var banners = this.presenter.getModelProperty('content_center.banners.content_slides');
      for (var i = 0; i < banners.length; i++) {
        if (banners[i].id === this.bannerModel.id) {
          this._bannerIndex = i;
          break;
        }
      }
    }
    return this._bannerIndex;
  };

  this.constructor(presenter, bannerModel);
}